import { useState } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useFetch, Field, fmtDate, Modal } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Empty, PageHeader } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FormattedText, FormatToolbar, useTextareaRef } from "@/components/FormattedText";
import { Plus, Trash2, ChevronDown, ChevronUp, Upload, Download, Paperclip, Pencil } from "lucide-react";

const EMPTY = { class_id: "", title: "", subject: "", description: "", due_date: "", send_email: true };

function StatusChip({ status }) {
  const { L } = useLang();
  if (status === "good") return <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="hw-status-good">🟢 {L("Good", "Bien")}</Badge>;
  if (status === "redo") return <Badge className="bg-red-600 hover:bg-red-600" data-testid="hw-status-redo">🔴 {L("À refaire", "À refaire")}</Badge>;
  return <Badge variant="secondary" data-testid="hw-status-pending">{L("Not graded", "Non noté")}</Badge>;
}

function Submissions({ hid, isAdmin }) {
  const { L } = useLang();
  const { data: subs, loading, reload } = useFetch(() => api.get(`/homework/${hid}/submissions`), [hid]);
  const [appr, setAppr] = useState({});
  const grade = async (sub, status) => {
    try {
      const r = await api.post(`/homework/submissions/${sub.id}/grade`, { status, appreciation: appr[sub.id] ?? sub.appreciation ?? "" });
      if (r.parent_alerted) toast.success(L("Parent alerted", "Parent alerté"));
      else toast.success(L("Saved", "Enregistré"));
      reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const resetEdits = async (sub) => {
    try { await api.post(`/homework/submissions/${sub.id}/grade`, { status: sub.status || "good", appreciation: sub.appreciation || "", reset: true }); toast.success(L("Edit counter reset", "Compteur réinitialisé")); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const uploadCorr = async (sub, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.raw.post(`/homework/submissions/${sub.id}/correction`, fd); toast.success(L("Correction sent", "Correction envoyée")); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delSub = async (sub) => {
    if (!window.confirm(L(`Are you sure you want to delete ${sub.student_name}'s submission?`, `Êtes-vous sûr de vouloir supprimer la remise de ${sub.student_name} ?`))) return;
    try { await api.del(`/homework/submissions/${sub.id}`); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  if (loading) return <p className="text-sm text-muted-foreground p-3">{L("Loading…", "Chargement…")}</p>;
  if (!subs || subs.length === 0) return <p className="text-sm text-muted-foreground p-3" data-testid={`hw-no-subs-${hid}`}>{L("No student submissions yet.", "Aucune remise d'élève pour l'instant.")}</p>;
  return (
    <div className="space-y-3 p-3 bg-secondary/30 rounded-b-lg" data-testid={`hw-subs-${hid}`}>
      {subs.map((sub) => {
        const editsUsed = sub.teacher_edit_count || 0;
        const remaining = Math.max(0, 3 - editsUsed);
        return (
          <div key={sub.id} className="bg-card border border-border rounded-lg p-3" data-testid={`hw-sub-${sub.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="font-medium">{sub.student_name}</span>
              <div className="flex items-center gap-2"><StatusChip status={sub.status} />
                {isAdmin && <Button size="icon" variant="ghost" onClick={() => delSub(sub)} data-testid={`hw-sub-del-${sub.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(sub.files || []).map((fl, i) => (
                <a key={i} href={`${API}/files/${fl.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline" data-testid={`hw-sub-file-${sub.id}-${i}`}><Paperclip className="h-3 w-3" />{fl.filename}</a>
              ))}
            </div>
            <div className="mt-3 grid gap-2">
              <Input placeholder={L("Appreciation / note (optional)", "Appréciation / mot (optionnel)")} defaultValue={sub.appreciation || ""} onChange={(e) => setAppr({ ...appr, [sub.id]: e.target.value })} data-testid={`hw-appr-${sub.id}`} />
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => grade(sub, "good")} data-testid={`hw-grade-good-${sub.id}`}>🟢 {L("Good", "Bien")}</Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => grade(sub, "redo")} data-testid={`hw-grade-redo-${sub.id}`}>🔴 {L("À refaire", "À refaire")}</Button>
                <label className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer">
                  <Upload className="h-3.5 w-3.5" />{L("Send correction", "Envoyer correction")}
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={(e) => uploadCorr(sub, e.target.files?.[0])} data-testid={`hw-corr-upload-${sub.id}`} />
                </label>
                {sub.correction && <a href={`${API}/files/${sub.correction.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline" data-testid={`hw-corr-link-${sub.id}`}><Download className="h-3 w-3" />{L("Correction", "Correction")}</a>}
              </div>
              <p className="text-[11px] text-muted-foreground" data-testid={`hw-edits-${sub.id}`}>
                {remaining > 0
                  ? L(`Teacher edits left: ${remaining}`, `Modifications restantes : ${remaining}`)
                  : L("Locked — saisie sans suppression. Admin only.", "Verrouillé — saisie sans suppression. Admin uniquement.")}
                {isAdmin && editsUsed >= 3 && <button className="ml-2 underline text-primary" onClick={() => resetEdits(sub)} data-testid={`hw-reset-${sub.id}`}>{L("Reset", "Réinitialiser")}</button>}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function HomeworkBoard({ isAdmin = false }) {
  const { L } = useLang();
  const { user } = useAuth();
  const canModify = user?.role === "admin" || user?.permissions?.academics === "manage";
  const { data: hw, loading, reload } = useFetch(() => api.get("/homework"));
  const { data: classes } = useFetch(() => api.get("/classes"));
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const descRef = useTextareaRef();
  const [expanded, setExpanded] = useState(null);
  const save = async () => {
    if (!f.class_id || !f.title.trim()) { toast.error(L("Class and title are required", "Classe et titre requis")); return; }
    try {
      if (editId) { await api.patch(`/homework/${editId}`, f); toast.success(L("Updated", "Mis à jour")); }
      else { const r = await api.post("/homework", f); toast.success(L(`Posted${r.emails_sent ? `, emailed ${r.emails_sent} parents` : ""}`, `Publié${r.emails_sent ? `, ${r.emails_sent} parents notifiés` : ""}`)); }
      setOpen(false); setEditId(null); setF(EMPTY); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const openEdit = (h) => { setEditId(h.id); setF({ class_id: h.class_id, title: h.title, subject: h.subject, description: h.description, due_date: h.due_date || "", send_email: false }); setOpen(true); };
  const del = async (h) => {
    if (!window.confirm(L(`Are you sure you want to delete "${h.title}" and all its submissions?`, `Êtes-vous sûr de vouloir supprimer « ${h.title} » et toutes ses remises ?`))) return;
    try { await api.del(`/homework/${h.id}`); toast.success(L("Deleted", "Supprimé")); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const uploadDoc = async (h, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.raw.post(`/homework/${h.id}/document`, fd); toast.success(L("Document uploaded", "Document téléversé")); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Homework", "Devoirs")} description={L("Post assignments, review student work, grade and send corrections.", "Publiez des devoirs, consultez les remises, notez et envoyez des corrections.")}
        action={<Button onClick={() => { setEditId(null); setF(EMPTY); setOpen(true); }} data-testid="add-homework-btn"><Plus className="h-4 w-4 mr-1" />{L("New Homework", "Nouveau devoir")}</Button>} />
      {(hw || []).length === 0 ? <Empty title={L("No homework yet", "Aucun devoir")} /> : (
        <div className="space-y-3">{hw.map((h) => (
          <div key={h.id} className="bg-card border border-border rounded-lg" data-testid={`hw-row-${h.id}`}>
            <div className="p-5">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <p className="font-heading font-bold">{h.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{h.subject} · {h.class_name} · {L("Due", "Pour le")} {fmtDate(h.due_date)}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant="secondary">{h.class_name}</Badge>
                  {canModify && <Button size="icon" variant="ghost" onClick={() => openEdit(h)} data-testid={`hw-edit-${h.id}`}><Pencil className="h-4 w-4" /></Button>}
                  {canModify && <Button size="icon" variant="ghost" onClick={() => del(h)} data-testid={`hw-del-${h.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                </div>
              </div>
              <FormattedText text={h.description} testid={`hw-desc-${h.id}`} />
              <div className="flex items-center gap-3 mt-3">
                <label className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer" data-testid={`hw-doc-upload-label-${h.id}`}>
                  <Upload className="h-3.5 w-3.5" />{L("Attach document", "Joindre un document")}
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={(e) => uploadDoc(h, e.target.files?.[0])} data-testid={`hw-doc-upload-${h.id}`} />
                </label>
                {h.attachment && <a href={`${API}/files/${h.attachment.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline" data-testid={`hw-doc-link-${h.id}`}><Download className="h-3 w-3" />{h.attachment.filename}</a>}
                <button className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-primary" onClick={() => setExpanded(expanded === h.id ? null : h.id)} data-testid={`hw-toggle-subs-${h.id}`}>
                  {L("Submissions", "Remises")} {expanded === h.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {expanded === h.id && <Submissions hid={h.id} isAdmin={isAdmin} />}
          </div>
        ))}</div>
      )}
      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? L("Edit Homework", "Modifier le devoir") : L("New Homework", "Nouveau devoir")} wide
        footer={<Button onClick={save} data-testid="save-homework-btn">{editId ? L("Save", "Enregistrer") : L("Post", "Publier")}</Button>}>
        <Field label={L("Class", "Classe")}>
          <Select value={f.class_id} onValueChange={(v) => setF({ ...f, class_id: v })}>
            <SelectTrigger data-testid="hw-class-select"><SelectValue placeholder={L("Select class", "Choisir une classe")} /></SelectTrigger>
            <SelectContent>{(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.grade_level}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={L("Title", "Titre")}><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="hw-title-input" /></Field>
          <Field label={L("Subject", "Matière")}><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="hw-subject-input" /></Field>
        </div>
        <Field label={L("Due date", "Pour le")}><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></Field>
        <Field label={L("Description", "Description")}>
          <FormatToolbar textareaRef={descRef} value={f.description || ""} onChange={(v) => setF({ ...f, description: v })} testid="hw-fmt" />
          <Textarea ref={descRef} rows={8} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="hw-desc-input" />
          <p className="text-xs text-muted-foreground mt-1">Ligne vide = nouveau paragraphe.</p>
        </Field>
        {!editId && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.send_email} onChange={(e) => setF({ ...f, send_email: e.target.checked })} data-testid="hw-email-check" />{L("Email parents", "Prévenir les parents")}</label>}
      </Modal>
    </div>
  );
}
